/**
 * Sovereign Admin Copilot Context & State Management
 * EPIC-OPS-003 / STORY-OPS-003.3 (AC-3.1 & AC-3.2)
 * 
 * ZERO PERSISTENT RESIDUE INVARIANT:
 * Conversation history remains strictly in React component state.
 * Never persisted to localStorage or sessionStorage. Purged cleanly on unmount/logout.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { queryAssistant, CopilotApiError } from '../services/assistantService';

export type CopilotStatus = 'idle' | 'loading' | 'error' | 'unavailable';
export type CopilotScope = 'PK' | 'UAE' | 'ALL';

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  latencyMs?: number;
  dataAsOf?: string;
}

export interface CopilotContextType {
  isOpen: boolean;
  status: CopilotStatus;
  messages: CopilotMessage[];
  scope: CopilotScope;
  errorMessage: string | null;
  errorCode: string | null;
  isAuthorized: boolean;
  toggleOpen: () => void;
  openCopilot: () => void;
  closeCopilot: () => void;
  setScope: (scope: CopilotScope) => void;
  submitQuery: (query: string) => Promise<void>;
  clearHistory: () => void;
}

const CopilotContext = createContext<CopilotContextType | undefined>(undefined);

export const CopilotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<CopilotStatus>('idle');
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Derive authorization & default scope
  const isSuperAdmin = user?.role === 'super_admin';
  const isRegionalAdmin = user?.role === 'regional_admin';
  const isAuthorized = isSuperAdmin || isRegionalAdmin;

  const defaultScope: CopilotScope = isSuperAdmin
    ? 'ALL'
    : user?.assignedRegion?.toUpperCase() === 'UAE'
    ? 'UAE'
    : 'PK';

  const [scope, setScopeState] = useState<CopilotScope>(defaultScope);

  // Sync default scope when user region or role changes
  useEffect(() => {
    setScopeState(defaultScope);
    // Purge in-memory messages when user identity changes (zero residue)
    setMessages([]);
    setStatus('idle');
    setErrorMessage(null);
    setErrorCode(null);
  }, [user?.id, user?.role, user?.assignedRegion]);

  const toggleOpen = useCallback(() => {
    if (!isAuthorized) return;
    setIsOpen((prev) => !prev);
  }, [isAuthorized]);

  const openCopilot = useCallback(() => {
    if (!isAuthorized) return;
    setIsOpen(true);
  }, [isAuthorized]);

  const closeCopilot = useCallback(() => {
    setIsOpen(false);
  }, []);

  const setScope = useCallback((newScope: CopilotScope) => {
    if (isSuperAdmin) {
      setScopeState(newScope);
    }
  }, [isSuperAdmin]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    setStatus('idle');
    setErrorMessage(null);
    setErrorCode(null);
  }, []);

  // Global Keyboard Shortcuts (Cmd/Ctrl + Shift + K, Escape) (AC-3.2)
  useEffect(() => {
    if (!isAuthorized) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape closes the drawer
      if (e.key === 'Escape') {
        if (isOpen) {
          setIsOpen(false);
          e.preventDefault();
        }
        return;
      }

      // Check for Cmd + Shift + K (Mac) or Ctrl + Shift + K (Windows/Linux)
      const isModifierPressed = e.metaKey || e.ctrlKey;
      const isShiftPressed = e.shiftKey;
      const isKPressed = e.key.toLowerCase() === 'k';

      if (isModifierPressed && isShiftPressed && isKPressed) {
        // Suppress shortcut if user is focused inside an input/textarea outside the copilot drawer
        const activeEl = document.activeElement as HTMLElement | null;
        const isExternalInput =
          activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.isContentEditable) &&
          !activeEl.closest('#copilot-drawer-container');

        if (isExternalInput) {
          return;
        }

        e.preventDefault();
        toggleOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthorized, isOpen, toggleOpen]);

  // Submit Query to Air-Gapped Backend
  const submitQuery = useCallback(async (queryText: string) => {
    const trimmed = queryText.trim();
    if (!trimmed || !isAuthorized) return;

    const userMsgId = `msg-user-${Date.now()}`;
    const userMsg: CopilotMessage = {
      id: userMsgId,
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMsg]);
    setStatus('loading');
    setErrorMessage(null);
    setErrorCode(null);

    try {
      const response = await queryAssistant({
        query: trimmed,
        scope: isSuperAdmin ? scope : undefined
      });

      const assistantMsg: CopilotMessage = {
        id: `msg-asst-${Date.now()}`,
        role: 'assistant',
        content: response.answer,
        timestamp: new Date().toISOString(),
        latencyMs: response.latencyMs,
        dataAsOf: response.dataAsOf
      };

      setMessages((prev) => [...prev, assistantMsg]);
      setStatus('idle');
    } catch (err: any) {
      const apiError = err as CopilotApiError;
      const code = apiError.code || 'UNKNOWN_ERROR';
      const message = apiError.message || 'Error executing Copilot query';

      setErrorCode(code);
      setErrorMessage(message);

      if (code === 'COPILOT_LOCAL_MODEL_UNAVAILABLE' || apiError.statusCode === 503) {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }
    }
  }, [isAuthorized, isSuperAdmin, scope]);

  const contextValue = useMemo<CopilotContextType>(() => ({
    isOpen,
    status,
    messages,
    scope,
    errorMessage,
    errorCode,
    isAuthorized,
    toggleOpen,
    openCopilot,
    closeCopilot,
    setScope,
    submitQuery,
    clearHistory
  }), [
    isOpen,
    status,
    messages,
    scope,
    errorMessage,
    errorCode,
    isAuthorized,
    toggleOpen,
    openCopilot,
    closeCopilot,
    setScope,
    submitQuery,
    clearHistory
  ]);

  return (
    <CopilotContext.Provider value={contextValue}>
      {children}
    </CopilotContext.Provider>
  );
};

export const useCopilot = (): CopilotContextType => {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return context;
};
